// Quick Refer
//
// Slim, focused list of Quick Referrals (records with referral_type === 3)
// for a selected time period. Visual layout matches the Formatted Data tab
// of Collaboration Analytics (date row, patient + DOB, referral_for,
// sending column, receiving column, status badge), but with no other tabs
// or aggregations — it's a fast clinical worklist.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import FacilityFilter from '../components/FacilityFilter';
import PersonContactHover from '../components/PersonContactHover';
import { PersonContactsProvider } from '../contexts/PersonContactsContext';
import client from '../api/client';
import { useCache } from '../hooks/useCache';
import { useCustomerFilter } from '../hooks/useCustomerFilter';
import { useScopeSync } from '../hooks/useScopeSync';
import { isTestRecord } from '../utils/testDataFilter';
import { safeDateString, dateMs } from '../utils/dateUtils';
import {
  attachmentSearchText,
  formatDicomLabel,
  formatDocumentsLabel,
  formatTagsLabel,
  hasDicom,
} from '../utils/referralAttachments';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { useScope } from '../contexts/ScopeContext';
import ScopeBar from '../components/ScopeBar';
import GroupFacilitiesModal from '../components/GroupFacilitiesModal';
import './CollaborationAnalytics.css';

const PAGE_SIZE = 100;
const COMPLETED_RX = /complete|closed|accepted|finished/i;

const f = (r, k) => (r?.data || r?.Data || {})[k];
const ts = (r) => r?.timestamp || (r?.data || r?.Data || {}).created_date;

// referral_type comes through as either a number (1, 2, 3, ...) or a string
// depending on the upstream serializer — accept both forms.
const isQuickReferral = (r) => {
  const v = f(r, 'referral_type');
  if (v === null || v === undefined) return false;
  return Number(v) === 3;
};

function calculateDateRange(preset) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'last_month':     start.setDate(start.getDate() - 30); break;
    case 'last_3_months':  start.setMonth(start.getMonth() - 3); break;
    case 'last_6_months':  start.setMonth(start.getMonth() - 6); break;
    case 'last_year':      start.setFullYear(start.getFullYear() - 1); break;
    case 'last_18_months': start.setMonth(start.getMonth() - 18); break;
    case 'last_2_years':   start.setFullYear(start.getFullYear() - 2); break;
    default: break;
  }
  return { start, end };
}
function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// 'YYYY-MM-DD' -> 'M/D/YYYY' for the inline date-range hint.
function formatDateShort(yyyymmdd) {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  if (!y || !m || !d) return yyyymmdd;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
}

function StatusBadge({ status }) {
  if (!status) return <span className="collab-status">—</span>;
  const s = String(status);
  const cls = COMPLETED_RX.test(s) ? 'collab-status collab-status-good'
    : /pending|new|open|in[ _-]?progress/i.test(s) ? 'collab-status collab-status-pending'
    : /declin|reject|cancel/i.test(s) ? 'collab-status collab-status-bad'
    : 'collab-status';
  return <span className={cls}>{s}</span>;
}

function QuickRefer() {
  const cache = useCache();

  const [dateRangePreset, setDateRangePreset] = useState('last_3_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState('');

  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const [activeCustomerId, setActiveCustomerId] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [withDicomOnly, setWithDicomOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [removeTestData, setRemoveTestData] = useState(true);

  // Compact filter strip vs full-form view, mirroring Customer Data
  // Analysis: starts expanded so first-time visitors see the dropdowns,
  // collapses to the slim summary the moment data loads.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  // Cross-page scope (drives navigation: scope set on this page sticks
  // across pages until cleared). The Clear button on the green Viewing
  // bar must wipe this too, otherwise navigating to another page would
  // re-load the cleared scope from sessionStorage.
  const { clearScope: clearGlobalScope } = useScope();

  // Single source of truth for "what is the user analyzing" so the
  // page title, the green scope bar, and any caption stay in sync.
  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  // Human label for the date-range preset shown in the compact strip.
  const dateRangeLabel = useMemo(() => {
    const presetNames = {
      last_month: 'Last 30 Days',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      last_year: 'Last 12 Months',
      last_18_months: 'Last 18 Months',
      last_2_years: 'Last 2 Years',
      custom: 'Custom Range',
    };
    const base = presetNames[dateRangePreset] || 'Date Range';
    if (startDate && endDate) {
      return `${base} (${formatDateShort(startDate)} - ${formatDateShort(endDate)})`;
    }
    return base;
  }, [dateRangePreset, startDate, endDate]);

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r.success) setFacilityHierarchy(r.hierarchy || []);
      } catch { /* hierarchy may not be cached yet */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* no filters yet */ }
    })();
    const { start, end } = calculateDateRange('last_3_months');
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = calculateDateRange(dateRangePreset);
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  }, [dateRangePreset]);

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) { setSelectedFacilityIds(new Set()); }
    else {
      const sf = savedFilters.find(s => s.id === parseInt(filterId, 10));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) { setSelectedFacilityIds(new Set()); }
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids));
    }
    setScopeFromCustomer(customerId);
  };

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) {
      setError('Please select a date range');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await cache.getCachedData('referrals', startDate, endDate);
      if (response.success) {
        setData(response.records || []);
        setFiltersCollapsed(true);
      } else {
        setError('Failed to load cached data');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch cached referrals. Have you refreshed this data type?');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, cache]);

  // Sticky scope: see useScopeSync.js. Keeps the StickyScopeBar in lock-step
  // with this page's customer / filter dropdowns and auto-loads when the
  // user arrives from a shortcut.
  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData,
    ready: Boolean(startDate && endDate),
  });

  // Facility ids actually present in the data (drives the picker tree).
  // Both ends of a referral count: the actual sender (sending_facility_id —
  // canonical name from DataCacheService normalization) and the destination
  // (receiving_facility_id). See REFERRAL-FIELD-NAMING.md.
  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    data.forEach(r => {
      const recv = f(r, 'receiving_facility_id');
      const send = f(r, 'sending_facility_id');
      if (recv != null) ids.add(Math.round(parseFloat(String(recv))).toString());
      if (send != null) ids.add(Math.round(parseFloat(String(send))).toString());
    });
    return ids;
  }, [data]);

  // 1) Quick referrals only (referral_type === 3)
  // 2) Optional facility filter — receiver-only (inbound). The page shows
  //    quick referrals coming INTO the selected facilities; an outbound
  //    quick referral (a customer's user fired one off to another VE
  //    account) is value to the receiver, not to us. Same rule applies
  //    whether the facility set came from a customer scope, a saved
  //    filter, or hand-picked.
  // 3) Optional free-text search across the visible columns
  const filtered = useMemo(() => {
    const facIds = selectedFacilityIds;
    const q = searchTerm.trim().toLowerCase();
    return data.filter(r => {
      if (!isQuickReferral(r)) return false;
      if (removeTestData && isTestRecord(r)) return false;
      if (withDicomOnly && !hasDicom(r)) return false;

      if (facIds.size > 0) {
        const recv = f(r, 'receiving_facility_id');
        const recvStr = recv != null ? String(recv) : null;
        if (!(recvStr && facIds.has(recvStr))) return false;
      }

      if (q) {
        const blob = [
          f(r, 'patient_first_name'),
          f(r, 'patient_last_name'),
          f(r, 'referral_for'),
          f(r, 'sending_user_name'),
          f(r, 'sending_facility'),
          f(r, 'sending_speciality'),
          f(r, 'creating_user_name'),
          f(r, 'creating_facility'),
          f(r, 'creating_speciality'),
          f(r, 'receiving_user_name'),
          f(r, 'receiving_facility'),
          f(r, 'receiving_speciality'),
          f(r, 'referral_number'),
          f(r, 'referral_status'),
          attachmentSearchText(r),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }

      return true;
    });
  }, [data, selectedFacilityIds, searchTerm, removeTestData, withDicomOnly]);

  // Newest first. dateMs unwraps SF date objects; raw `new Date(obj)`
  // on those returns Invalid Date and silently breaks the sort order.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => dateMs(ts(b)) - dateMs(ts(a)));
  }, [filtered]);

  useEffect(() => { setPage(1); }, [sorted.length, searchTerm, withDicomOnly]);

  const lastPage = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sorted, page]
  );

  // Staff-submitter deduction. The quick-refer form auto-creates one VE user
  // per form contact at the temp sending facility, so the user at that
  // facility who is NOT the sending provider is the staff member who keyed
  // the referral in. No second user = the provider submitted it themselves.
  const [facilityUsers, setFacilityUsers] = useState({});
  const sendingFacilityIds = useMemo(() => {
    const ids = new Set();
    for (const r of sorted) {
      const id = Number(f(r, 'sending_facility_id'));
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return [...ids];
  }, [sorted]);

  useEffect(() => {
    if (sendingFacilityIds.length === 0) { setFacilityUsers({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await client.post('/api/cache/users/by-facility', {
          facilityIds: sendingFacilityIds,
        });
        if (!cancelled) setFacilityUsers(res.data?.facilities || {});
      } catch (e) {
        console.error('Facility user lookup failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [sendingFacilityIds]);

  // Order-insensitive name fingerprint so "Thibodeaux, Lara" matches
  // "Lara Thibodeaux" when excluding the provider from staff candidates.
  const nameKey = (s) => String(s || '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean)
    .sort()
    .join(' ');

  // The staff submitter for one referral row, or null when it can't be
  // deduced unambiguously (no second user, or several candidates — e.g. a
  // real registered facility with many users).
  const deduceStaff = (r) => {
    const facId = Number(f(r, 'sending_facility_id'));
    const users = facilityUsers[String(facId)];
    if (!users || users.length === 0) return null;
    const senderId = Number(f(r, 'sending_user_id'));
    const senderKey = nameKey(f(r, 'sending_user_name'));
    const candidates = users.filter(u =>
      Number(u.userId) !== senderId
      && (!senderKey || nameKey(u.displayName) !== senderKey));
    return candidates.length === 1 ? candidates[0] : null;
  };

  // User IDs visible in the table — batch-fetched for the contact hover
  // cards (same machinery as the Insights People/Spokes tabs). Deduced
  // staff submitters are included so their hover cards resolve too.
  const personUserIds = useMemo(() => {
    const ids = new Set();
    for (const r of sorted) {
      for (const key of ['sending_user_id', 'creating_user_id', 'receiving_user_id']) {
        const id = Number(f(r, key));
        if (Number.isFinite(id) && id > 0) ids.add(id);
      }
    }
    for (const users of Object.values(facilityUsers)) {
      for (const u of users || []) {
        const id = Number(u.userId);
        if (Number.isFinite(id) && id > 0) ids.add(id);
      }
    }
    return [...ids];
  }, [sorted, facilityUsers]);

  // Tracker export: reproduces the "Referral Data" tab of Randall's
  // "Spoke & Hub TP Tracker" workbook from the rows currently displayed
  // (all pages, after filters/search). Provider phone/email populate
  // automatically once the VE users API is authorized for this hospital.
  const [exporting, setExporting] = useState(false);
  const exportTracker = async () => {
    const ids = sorted
      .map(r => Number(f(r, 'referral_id')))
      .filter(n => Number.isFinite(n) && n > 0);
    if (ids.length === 0) return;
    setExporting(true);
    setError('');
    try {
      const res = await client.post(
        '/api/cache/quick-referrals/export',
        { referralIds: ids },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Spoke-Hub-TP-Tracker-${startDate}-to-${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.message || 'request error';
      // Axios gives a Blob body on responseType:'blob' errors — unwrap JSON
      // { error: "..." } so the banner shows the real server message.
      try {
        const data = e?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) detail = parsed.error;
        } else if (data?.error) {
          detail = data.error;
        }
      } catch { /* keep axios message */ }
      setError(`Tracker export failed: ${detail}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>
          Quick Refer
          <span className="page-header-scope">
            <span className="page-header-sep">&mdash;</span>
            <span className="page-header-name">{currentScope.name}</span>
          </span>
        </h1>
      </div>

      {filtersCollapsed && data.length > 0 ? (
        <div className="filter-strip-bar">
          <div className="filter-strip-summary">
            <div className="filter-strip-item">
              <span className="filter-strip-label">Date range</span>
              <span className="filter-strip-value">{dateRangeLabel}</span>
            </div>
            <div className="filter-strip-item">
              <span className="filter-strip-label">Test data</span>
              <span
                className="filter-strip-value"
                title="When 'Hidden', records flagged as test data are filtered out."
              >
                {removeTestData ? 'Hidden' : 'Shown'}
              </span>
            </div>
          </div>
          <div className="filter-strip-actions">
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-ghost"
              onClick={() => setFiltersCollapsed(false)}
            >
              Edit filters
            </button>
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-primary"
              onClick={loadData}
              disabled={loading || !startDate || !endDate}
            >
              {loading ? 'Loading...' : 'Reload'}
            </button>
          </div>
        </div>
      ) : (
        <div className="controls-panel">
          <div className="date-range-selector">
            <label htmlFor="qrDatePreset">Date Range:</label>
            <select
              id="qrDatePreset"
              value={dateRangePreset}
              onChange={(e) => setDateRangePreset(e.target.value)}
              disabled={loading}
              className="date-range-dropdown"
            >
              <option value="last_month">Last 30 Days</option>
              <option value="last_3_months">Last 3 Months</option>
              <option value="last_6_months">Last 6 Months</option>
              <option value="last_year">Last 12 Months</option>
              <option value="last_18_months">Last 18 Months</option>
              <option value="last_2_years">Last 2 Years</option>
              <option value="custom">Custom Range</option>
            </select>
            {startDate && endDate && (
              <span
                className="date-range-hint"
                title="The exact start and end dates this range resolves to. All presets are trailing windows ending today."
              >
                {formatDateShort(startDate)} &ndash; {formatDateShort(endDate)}
              </span>
            )}
          </div>

          {dateRangePreset === 'custom' && (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              disabled={loading}
            />
          )}

          {savedFilters.length > 0 && (
            <div className="saved-filter-selector">
              <label htmlFor="qrSavedFilter">Saved Filter:</label>
              <select
                id="qrSavedFilter"
                value={activeFilterId}
                onChange={(e) => applySavedFilter(e.target.value)}
                className="date-range-dropdown"
              >
                <option value="">None (show all)</option>
                {savedFilters.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.facilityIds?.length || 0})
                  </option>
                ))}
              </select>
            </div>
          )}

          {customers.length > 0 && (
            <div className="saved-filter-selector">
              <label htmlFor="qrCustomerFilter">Customer:</label>
              <select
                id="qrCustomerFilter"
                value={activeCustomerId}
                onChange={(e) => applyCustomerFilter(e.target.value)}
                className="date-range-dropdown"
              >
                <option value="">None (show all)</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c._depth > 0 ? '— ' : ''}{c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button className="filter-button" onClick={() => setShowFacilityFilter(true)}>
            Custom Filter
            {selectedFacilityIds.size > 0 && ` (${selectedFacilityIds.size})`}
          </button>

          <label
            className="collab-test-toggle"
            title="Hide referrals flagged as test data, with patient names like TEST/TESTLEY/John Doe, or sent to/from VitalEngine or MyHeart facilities."
          >
            <input
              type="checkbox"
              checked={removeTestData}
              onChange={(e) => setRemoveTestData(e.target.checked)}
            />
            Remove Test Data
          </label>

          <button
            className="load-button"
            onClick={loadData}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Load Data'}
          </button>

          {error && <div className="error-message">{error}</div>}
          {cache.error && <div className="error-message">{cache.error}</div>}

          {data.length > 0 && (
            <div className="data-info">
              <strong>{sorted.length}</strong> quick referrals from <strong>{data.length}</strong> total records
            </div>
          )}
        </div>
      )}

      {filtersCollapsed && (error || cache.error) && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error || cache.error}
        </div>
      )}

      {filtersCollapsed && data.length > 0 && (
        <div className="data-info" style={{ marginBottom: '1rem' }}>
          <strong>{sorted.length}</strong> quick referrals from <strong>{data.length}</strong> total records
        </div>
      )}

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && facilityHierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={facilityHierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {data.length === 0 ? (
        <div className="chart-panel">
          <div className="empty-state">
            <p>No data loaded yet.</p>
            <p>
              Pick a date range above and press <strong>Load Data</strong>.
              An administrator can populate the local cache from <strong>Admin → Data Sources</strong>.
            </p>
          </div>
        </div>
      ) : (
        <PersonContactsProvider userIds={personUserIds}>
        <div className="collab-tab-body">
          <div className="collab-toolbar">
            <input
              type="search"
              className="collab-search"
              placeholder="Search patient, referral, provider, facility, tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <label className={`collab-filter-toggle ${withDicomOnly ? 'on' : ''}`} title="Show only quick referrals with at least one DICOM study attached">
              <input
                type="checkbox"
                checked={withDicomOnly}
                onChange={(e) => setWithDicomOnly(e.target.checked)}
              />
              With DICOM
            </label>
            <button
              type="button"
              className="collab-export-btn"
              onClick={exportTracker}
              disabled={exporting || sorted.length === 0}
              title="Download the Spoke & Hub TP Tracker workbook built from the rows below. Provider phone/email columns fill in automatically once the VitalEngine users API is enabled."
            >
              {exporting ? 'Building…' : 'Download Tracker (.xlsx)'}
            </button>
            <span className="collab-count">
              {sorted.length.toLocaleString()} quick referrals
            </span>
          </div>

          <div className="collab-table-wrap">
            <table className="collab-table collab-formatted">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Referral For</th>
                  <th>Sending</th>
                  <th>Creating</th>
                  <th>Receiving</th>
                  <th>Images</th>
                  <th>Tags</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr><td colSpan={9} className="collab-empty">No quick referrals match the current filters.</td></tr>
                )}
                {pageRows.map((r, i) => {
                  // safeDateString tolerates the Salesforce date-object shape
                  // ({ year, month, day, value, ... }) that occasionally slips
                  // through unnormalized — rendering it raw throws React #31.
                  const date = safeDateString(ts(r)) || '—';
                  const fn = f(r, 'patient_first_name') || '';
                  const ln = f(r, 'patient_last_name') || '';
                  const dob = safeDateString(f(r, 'patient_dob'));
                  const referralFor = f(r, 'referral_for') || '';

                  // sending_* / creating_* / receiving_* are the canonical role names
                  // produced by DataCacheService. See REFERRAL-FIELD-NAMING.md.
                  const sendUser = f(r, 'sending_user_name');
                  const sendSpec = f(r, 'sending_speciality');
                  const sendFac  = f(r, 'sending_facility');
                  const hasSend  = sendUser || sendSpec || sendFac;

                  // Always show typist data when present (matches VE's spreadsheet —
                  // their Created By column is populated for every row, even when
                  // the typist is the same person as the sender).
                  const createUser = f(r, 'creating_user_name');
                  const createSpec = f(r, 'creating_speciality');
                  const createFac  = f(r, 'creating_facility');
                  const hasCreate  = createUser || createFac;

                  // Quick-refer staff submitter. Explicit fields from the API
                  // win if VE ever exposes them; otherwise deduce from the
                  // user directory (the second user auto-created at the temp
                  // sending facility).
                  const staffName = f(r, 'referring_staff_name') || f(r, 'submitter_name') || f(r, 'staff_name');
                  const staffEmail = f(r, 'referring_staff_email') || f(r, 'submitter_email') || f(r, 'staff_email');
                  const deduced = (!staffName && !staffEmail) ? deduceStaff(r) : null;

                  return (
                    <tr key={`${f(r, 'referral_id') || i}-${i}`}>
                      <td className="collab-cell-date">{date}</td>
                      <td>
                        <div className="collab-patient-name">{fn} {ln}</div>
                        {dob && <div className="collab-patient-dob">DOB: {dob}</div>}
                      </td>
                      <td>{referralFor || '—'}</td>
                      <td>
                        {hasSend ? (
                          <>
                            <div className="collab-prov-name">
                              {sendUser
                                ? <PersonContactHover userId={f(r, 'sending_user_id')}>{sendUser}</PersonContactHover>
                                : '—'}
                            </div>
                            {sendSpec && <div className="collab-prov-meta">{sendSpec}</div>}
                            {sendFac  && <div className="collab-prov-meta">{sendFac}</div>}
                          </>
                        ) : '—'}
                      </td>
                      <td>
                        {deduced ? (
                          // Staff submitter deduced from the user directory —
                          // replaces the API's creating user, which is always
                          // a duplicate of the sending provider for quick
                          // referrals.
                          <>
                            <div className="collab-prov-name">
                              <PersonContactHover userId={deduced.userId}>
                                {deduced.displayName || '—'}
                              </PersonContactHover>
                            </div>
                            {createFac && <div className="collab-prov-meta">{createFac}</div>}
                            <div className="collab-prov-meta">Staff submitter</div>
                          </>
                        ) : hasCreate ? (
                          <>
                            <div className="collab-prov-name">
                              {createUser
                                ? <PersonContactHover userId={f(r, 'creating_user_id')}>{createUser}</PersonContactHover>
                                : '—'}
                            </div>
                            {createSpec && <div className="collab-prov-meta">{createSpec}</div>}
                            {createFac  && <div className="collab-prov-meta">{createFac}</div>}
                            {staffName && (
                              <div className="collab-prov-meta">Staff: {staffName}</div>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td>
                        <div className="collab-prov-name">
                          {f(r, 'receiving_user_name')
                            ? <PersonContactHover userId={f(r, 'receiving_user_id')}>{f(r, 'receiving_user_name')}</PersonContactHover>
                            : '—'}
                        </div>
                        <div className="collab-prov-meta">{f(r, 'receiving_speciality') || ''}</div>
                        <div className="collab-prov-meta">{f(r, 'receiving_facility') || ''}</div>
                      </td>
                      <td>
                        <div className="collab-prov-name">{formatDicomLabel(r)}</div>
                        {formatDocumentsLabel(r) && (
                          <div className="collab-prov-meta">{formatDocumentsLabel(r)}</div>
                        )}
                      </td>
                      <td>{formatTagsLabel(r)}</td>
                      <td><StatusBadge status={f(r, 'referral_status')} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {lastPage > 1 && (
            <div className="collab-pager">
              <button onClick={() => setPage(1)}              disabled={page <= 1}>« First</button>
              <button onClick={() => setPage(page - 1)}       disabled={page <= 1}>‹ Prev</button>
              <span>Page {page} of {lastPage}</span>
              <button onClick={() => setPage(page + 1)}       disabled={page >= lastPage}>Next ›</button>
              <button onClick={() => setPage(lastPage)}       disabled={page >= lastPage}>Last »</button>
            </div>
          )}
        </div>
        </PersonContactsProvider>
      )}

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={facilityHierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => { setSelectedFacilityIds(ids); setActiveFilterId(''); setActiveCustomerId(''); }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </>
  );
}

export default QuickRefer;
